import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import DocumentTitle from 'react-document-title'
import qs from 'qs'
import Icon from '../../components/misc/Icons'
import { lookupPlayerAuthCode, approvePlayerAuthCode } from '../../actions/playerAuth'
import getErrorMessageForRequest from '../../utils/getErrorMessageForRequest'
import ModalWrapper from '../../components/modals/ModalWrapper'
import { hideAuthPlayerModal } from '../../utils/authPlayer/hideAuthPlayerModal'

//
// Pairs a standalone player (play.plickers.com) to this account.
//
// Reached only by typing the address or by the QR code on the player's screen -
// there is deliberately no link to it anywhere in the app.
//
// Shown as a modal over the app rather than as a page of its own, on the same
// terms as Now Playing: visible at the path `/auth-player` or at the hash
// `#auth-player`, so it can be landed on cold or opened in place. Connecting a
// screen is a detour from whatever the teacher was doing, and a modal is what
// lets them carry on afterwards.
//
// Two steps on purpose. Looking a code up shows where the pairing request came
// from without granting anything, so a request the teacher did not expect is
// something they can see and walk away from rather than confirm by reflex. Only
// the second step hands over an account.
//

const CODE_LENGTH = 8
const GROUP_LENGTH = 4

/** `ktq48mrw` as typed becomes `KTQ4-8MRW` as displayed. */
function formatCodeInput(raw) {
  const bare = (raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, CODE_LENGTH)
  if (bare.length <= GROUP_LENGTH) return bare
  return `${bare.slice(0, GROUP_LENGTH)}-${bare.slice(GROUP_LENGTH)}`
}

function isComplete(code) {
  return (code || '').replace(/[^A-Z0-9]/g, '').length === CODE_LENGTH
}

class AuthPlayerModal extends Component {
  constructor(props) {
    super(props)
    const params = qs.parse(props.location.search.slice(1))
    this.state = {
      // 'enterCode' | 'looking' | 'confirm' | 'approving' | 'success' | 'error'
      status: 'enterCode',
      code: formatCodeInput(params.code),
      request: null,
      errorTitle: '',
      errorMessage: '',
    }
    this.handleCodeChange = this.handleCodeChange.bind(this)
    this.handleSubmitCode = this.handleSubmitCode.bind(this)
    this.handleApprove = this.handleApprove.bind(this)
    this.handleStartOver = this.handleStartOver.bind(this)
    this.handleDismiss = this.handleDismiss.bind(this)
  }

  componentDidMount() {
    // The player's QR code carries the pairing code, so a scan lands here ready
    // to confirm rather than ready to type.
    if (isComplete(this.state.code)) {
      this.handleSubmitCode()
    }
  }

  handleCodeChange(e) {
    this.setState({ code: formatCodeInput(e.target.value) })
  }

  handleSubmitCode(e) {
    if (e) e.preventDefault()
    const { code } = this.state
    if (!isComplete(code)) return

    this.setState({ status: 'looking' })

    this.props.lookupPlayerAuthCode(code)
      .then((request) => {
        this.setState({ request, status: 'confirm' })
      })
      .catch((err) => {
        this.setState({
          status: 'error',
          errorTitle: 'That code did not work',
          errorMessage: getErrorMessageForRequest({
            requestName: 'lookupPlayerAuthCode',
            response: err.response,
          }),
        })
      })
  }

  handleApprove() {
    this.setState({ status: 'approving' })

    this.props.approvePlayerAuthCode(this.state.code)
      .then(() => {
        this.setState({ status: 'success' })
      })
      .catch((err) => {
        this.setState({
          status: 'error',
          errorTitle: 'Could not connect that screen',
          errorMessage: getErrorMessageForRequest({
            requestName: 'approvePlayerAuthCode',
            response: err.response,
          }),
        })
      })
  }

  //
  // Connecting a screen is a detour, not a destination: a teacher who paired
  // one mid-lesson wants to be back where they were, not stranded on a page
  // whose only instruction is to close the tab.
  //
  handleDismiss() {
    hideAuthPlayerModal(this.props.history)
  }

  handleStartOver() {
    this.setState({
      status: 'enterCode', code: '', request: null, errorTitle: '', errorMessage: '',
    })
  }

  renderRequestInfo() {
    const { request } = this.state
    if (!request || !request.requestInfo) return null
    const { ip, geo } = request.requestInfo
    const place = geo ? [geo.city, geo.region, geo.country].filter(Boolean).join(', ') : ''

    return (
      <div className='authPlayerPage-requestInfo'>
        <div className='authPlayerPage-requestInfo-title'>
          Where this request came from
        </div>
        <table className='authPlayerPage-requestInfo-table'>
          <tbody>
            {place && (
              <tr>
                <td className='authPlayerPage-requestInfo-label'>Location</td>
                <td className='authPlayerPage-requestInfo-value'>{place}</td>
              </tr>
            )}
            {ip && (
              <tr>
                <td className='authPlayerPage-requestInfo-label'>IP address</td>
                <td className='authPlayerPage-requestInfo-value'>{ip}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    )
  }

  render() {
    const {
      status, code, errorTitle, errorMessage,
    } = this.state

    return (
      <DocumentTitle title='Plickers - Connect a Screen'>
        <div className='authPlayerModal'>
          {/* ModalWrapper's header, its Cancel button and the floating close
              icon all default to true. The card carries its own Close, so none
              of them are wanted here. */}
          <ModalWrapper
            modalStyle='light'
            showModalHeader={false}
            cancelBtn={false}
            closeBtn={false}
            hideModal={this.handleDismiss}
          >
            <div className='authPlayerPage-card marketing'>
              <div className='authPlayerPage-logo'>
                <Icon name='logo-full' />
              </div>
              <div className='authPlayerPage-divider' />

              {(status === 'enterCode' || status === 'looking') && (
              <React.Fragment>
                <div className='authPlayerPage-title'>
                  Connect a screen
                </div>
                <div className='authPlayerPage-message'>
                  Enter the code shown on the screen you want to play through.
                </div>
                <form onSubmit={this.handleSubmitCode}>
                  <input
                    className='authPlayerPage-codeInput'
                    value={code}
                    onChange={this.handleCodeChange}
                    placeholder='XXXX-XXXX'
                    autoComplete='off'
                    autoCorrect='off'
                    spellCheck='false'
                    autoFocus
                    disabled={status === 'looking'}
                  />
                  <button
                    type='submit'
                    className='authPlayerPage-btn authPlayerPage-btn--primary'
                    disabled={!isComplete(code) || status === 'looking'}
                  >
                    {status === 'looking' ? 'Checking…' : 'Continue'}
                  </button>
                </form>
              </React.Fragment>
              )}

              {(status === 'confirm' || status === 'approving') && (
              <React.Fragment>
                <div className='authPlayerPage-title'>
                  Is this the screen in front of you?
                </div>
                <div className='authPlayerPage-message'>
                  Connecting lets that screen show your classes and slides, and
                  follow along as you play. It cannot change your account.
                </div>
                {this.renderRequestInfo()}
                <button
                  type='button'
                  className='authPlayerPage-btn authPlayerPage-btn--primary'
                  onClick={this.handleApprove}
                  disabled={status === 'approving'}
                >
                  {status === 'approving' ? 'Connecting…' : 'Yes, connect it'}
                </button>
                <button
                  type='button'
                  className='authPlayerPage-btn authPlayerPage-btn--secondary'
                  onClick={this.handleStartOver}
                  disabled={status === 'approving'}
                >
                  No, cancel
                </button>
              </React.Fragment>
              )}

              {status === 'success' && (
              <React.Fragment>
                <div className='authPlayerPage-title'>
                  Screen connected
                </div>
                <div className='authPlayerPage-message'>
                  It should be ready in a moment. You can carry on here.
                </div>
              </React.Fragment>
              )}

              {status === 'error' && (
              <React.Fragment>
                <div className='authPlayerPage-title'>
                  {errorTitle}
                </div>
                <div className='authPlayerPage-message'>
                  {errorMessage}
                </div>
                <button
                  type='button'
                  className='authPlayerPage-btn authPlayerPage-btn--primary'
                  onClick={this.handleStartOver}
                >
                  Try another code
                </button>
              </React.Fragment>
              )}
              {/*
                * The confirm step asks a yes/no question and already offers
                * "No, cancel", so a Close beside it is a third answer to a
                * two-answer question. Every other step needs a way out.
                */}
              {status !== 'confirm' && status !== 'approving' && (
              <button
                type='button'
                className='authPlayerPage-closeBtn'
                onClick={this.handleDismiss}
              >
                Close
              </button>
              )}
            </div>
          </ModalWrapper>
        </div>
      </DocumentTitle>
    )
  }
}

//
// Visible at the path or at the hash, the same test LiveViewModalContainer
// makes. The modal is mounted only while it is visible rather than hidden with
// CSS: its constructor reads `?code=` and submits it, which must happen when the
// teacher arrives, not once at app start.
//
export function isAuthPlayerVisible(location) {
  return location.pathname === '/auth-player' ||
    location.pathname === '/auth-player/' ||
    location.hash === '#auth-player'
}

class AuthPlayerContainer extends Component {
  render() {
    const { location } = this.props
    if (!isAuthPlayerVisible(location)) return null
    const { history, lookupPlayerAuthCode: lookup, approvePlayerAuthCode: approve } = this.props
    return (
      <AuthPlayerModal
        // Keyed on the location so arriving with a different code starts over
        // rather than reusing the state of the last attempt.
        key={location.pathname + location.search}
        location={location}
        history={history}
        lookupPlayerAuthCode={lookup}
        approvePlayerAuthCode={approve}
      />
    )
  }
}

export default withRouter(connect(
  () => ({}),
  { lookupPlayerAuthCode, approvePlayerAuthCode },
)(AuthPlayerContainer))
